import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';
import { LoadingProvider } from '@app/components/_shared/loading-spinner/loading-spinner.component';
import { ConnectionService } from '@app/services/connection.service';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';

import { AppConfig } from './app.config';
import { MainLayoutComponent } from './components/_shared/main-layout/main-layout.component';
import { ModalProvider } from './components/_shared/modal/modal.component';
import { ApiResponse, ApiReturnCode } from './models/_base/apiResponse.model';
import { AuthService } from './services/auth.service';
import { CurrentQuestionnaireService } from './services/current-questionnaire.service';
import { Error, ErrorService } from './services/error.service';
import { GoogleAnalyticsEventsService } from './services/google-analytics-events.service';
import { LocalisationService } from './services/localisation.service';
import { QuestionnaireService } from './services/questionnaire.service';
import { UserService } from './services/user.service';
import { WindowUtils } from './utils/windowUtils';

@Component({
  selector: 'body',
  host: { '[class.gradient]': 'gradient', '[class.main]': '!gradient' },
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  gradient: boolean;
  locale: any = {};

  private _routerSub;

  private versionData = new BehaviorSubject<string>('0');
  private lastVersionCheckTime: Date = new Date();

  private currentVersion;
  private versionFile;

  constructor(
    private httpService: HttpClient,
    private router: Router,
    private modal: ModalProvider,
    private localisation: LocalisationService,
    private questionnaireService: QuestionnaireService,
    private currentQuestionnaire: CurrentQuestionnaireService,
    private userService: UserService,
    private authService: AuthService,
    private errorService: ErrorService,
    private connectionService: ConnectionService,
    public googleAnalyticsEventsService: GoogleAnalyticsEventsService,
    private loadingProvider: LoadingProvider,
    private toastr: ToastrService
  ) {
    this.locale = this.localisation.getTranslationsForComponent('AppComponent');
    WindowUtils.initialise();
    connectionService.connectionLost.subscribe(status => {
      loadingProvider.hide();
      switch (status) {
        case this.connectionService.CONNECTION_LOST: {
          toastr.error(this.locale.connectionLostBody, this.locale.connectionLostTitle);
          break;
        }

        case this.connectionService.SERVER_ERROR: {
          toastr.error(this.locale.serverErrorBody, this.locale.serverErrorTitle);
          break;
        }

        case this.connectionService.NO_ERRORS: {
          toastr.clear();
          break;
        }
      }
    });
  }

  ngOnInit() {
    this.loadScript();
    console.log(
      '%c MyUniChoices version:' + '%c ' + AppConfig.version,
      'color: #76cbcc;',
      'color: #23898b;'
    );

    if (!document.cookie.includes('state=1')) {
      this.authService.logout(false);
    }

    this._routerSub = this.router.events
      .filter(event => event instanceof NavigationEnd)
      .subscribe(val => {
        this.gradient = !(val as NavigationEnd).url.startsWith(`/${MainLayoutComponent.route}`);
        window.scrollTo(0, 0);

        if (val instanceof NavigationEnd) {
          window['dataLayer'].push({ event: 'page_view' });
        }
      });
  }

  ngDestroy() {
    this._routerSub.unsubscribe();
  }

  loadScript() {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML =
      "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':" +
      " new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=" +
      " 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','" +
      AppConfig.gtmId +
      "');";
    const head = document.getElementsByTagName('head')[0];
    if (head !== null && head !== undefined) {
      document.head.appendChild(script);
    }
  }

  redirectToRoute(route: string) {
    this.router.navigate([route]);
  }

  showErrorModal(err: ApiResponse) {
    if (this.filterError(err)) {
      this.modal.create(this.getErrorTitle(err.returnCode), this.getErrorMessage(err.returnCode));
    }
  }

  private getErrorMessage(returnCode: ApiReturnCode): string {
    switch (returnCode.label) {
      case 'E_EMAIL_TAKEN':
        return `This email is already registered with an account.`;
      default:
        return returnCode.message;
    }
  }

  private getErrorTitle(returnCode: ApiReturnCode): string {
    switch (returnCode.label) {
      case 'E_EMAIL_TAKEN':
        return `Hello Again`;
      default:
        return returnCode.type;
    }
  }

  private filterError(err: ApiResponse): boolean {
    switch (err.returnCode.label) {
      case 'E_NO_TOKEN_PROVIDED':
      case 'E_TOKEN_EXPIRED':
      case 'E_TOKEN_INVALID':
      case 'E_USER_NOT_FOUND':
        this.router.navigateByUrl('/login');
        return false;
      case 'E_NO_AREAS_SELECTED':
        sessionStorage.setItem('areas-incomplete', '1');
        this.router.navigateByUrl('/centigrade/course-areas');
        return false;
      case 'E_FILTER_INCOMPLETE':
        sessionStorage.setItem('filter-incomplete', '1');
        this.router.navigateByUrl('/centigrade/course-area-filters');
        return false;
      case 'E_PAUSED_ACCESS':
      case 'E_PAUSED_ACCESS_QUESTIONNAIRE':
        this.errorService.updateError({
          code: err.returnCode.label,
          title: 'Paused Access',
          message:
            'Your school or adviser has restricted access to this part of the app until a later date.'
        } as Error);
        this.router.navigateByUrl('/error');
        return false;
      case 'E_MAINTENANCE_MODE':
        this.errorService.updateError({
          code: err.returnCode.label,
          title: "We're in Maintenance Mode",
          message:
            "We're making some exciting new updates to our site. We'll be back online shortly!"
        } as Error);
        this.router.navigateByUrl('/error');
        return false;
      default:
    }
    return true;
  }

  private getErrorTemplate(errorLabel: string): string {
    switch (errorLabel) {
      case 'E_COURSE_ID_INVALID':
        return `<a onclick="window.history.back()">Back</a>`;
      case 'E_FILTER_INCOMPLETE':
        return `<a routerLink="/centigrade/course-area-filters">Complete preferences</a>`;
      case 'E_NO_AREAS_SELECTED':
        return `<a routerLink="/centigrade/course-areas">Select course areas</a>`;
      case 'E_USER_NOT_ACTIVE':
        return `<a routerLink="/resend-activation">I didn't receive my activation email</a>`;
      default:
        return null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts